import "./ArticleCard.scss";

import React from "react";
import { useSelectBundle } from "../../hooks/useSelectBundle";
import { jsxWithIntl } from "shared/src/i18n/IntlRootMixin";
import type { ArticleCardProps } from "shared/src/types/ArticleCardProps";
import { AddToListDropdown } from "shared/src/components/AddToList/AddToListDropdown";
import PriceDisplay from "../PriceDisplay/PriceDisplay";
import { useDisplayConfig } from "../../hooks/useDisplayConfig";
import { BundleSelectorDropdown } from "shared/src/components/BundleSelectorDropdown/BundleSelectorDropdown";
import CustomerDisplayId from "../CustomerDisplayId/CustomerDisplayId";
import DnRDisplay from "../DnRDisplay/DnRDisplay";
import { primaryIsNet } from "../../../src/utils/PriceDisplayFramework";
import { PromotionLabelList } from "shared/src/components/PromotionLabels/PromotionLabelList";
import { AddToBasketControl } from "../AddToBasket/AddToBasketControl";
import SearchResultDebugInfoPanel from "../DebugFunction/SearchResultDebugInfo";
import { AvailabilityDetails } from "shared/src/components/AvailabilityDetails/AvailabilityDetails";
import extractDeliveryDate from "shared/src/utils/extractAvailabilityData";
import Description from "../Description/Description";
import ImageDisplay from "../ImageDisplay/ImageDisplay";
import { PageType } from "shared/src/types/PageType";
import { getShowsSummaryDnrInfo } from "shared/src/utils/articleUtil";
import { getPricesDisplayData } from "shared/src/utils/PriceDisplayFramework";
import { BasketAddTrackingTags } from "shared/src/tracking/BasketTrackingUtil";

function ArticleCard({
    variant,
    preselectedBundleId,
    callbacks,
    selectedDeliveryAddress,
    articleDeprecated,
    eventUuidGenerator,
    displayConfig,
    priceAndPromotionInfos,
    basketInfo,
    debugInformation,
    optionalTrackingTags
}: ArticleCardProps) {
    const {
        eventId,
        pdpLink,
        selectedBundle,
        selectedPriceAndPromotionInfo,
        selectedStoreData,
        selectedAmountInBasket,
        selectedAmountInDnr,
        sortedBundleSelector,
        setChangedBundleId
    } = useSelectBundle(
        eventUuidGenerator,
        variant.bundles,
        variant.bundleSelector,
        preselectedBundleId,
        priceAndPromotionInfos,
        displayConfig.selectedStoreId,
        displayConfig.isFsdShop,
        displayConfig.isMarioCountry,
        displayConfig.isEmployee,
        basketInfo.amountById
    );

    const { internalDisplayConfig } = useDisplayConfig(displayConfig);

    const onAddToCart = (amount: number, bettyBundleId: string | null | undefined) => {
        callbacks.onAddToCart(eventId, amount, bettyBundleId);
    };

    // Augmenting tracking callbacks by eventId
    const onAddToListCreateNewList = () => {
        return callbacks.onAddToListCreateNewList();
    };
    const onListMenuItemSelect = () => {
        return callbacks.onListMenuItemSelect();
    };

    const onBundleSelect = (selectedBettyBundleId: string) => {
        setChangedBundleId(selectedBettyBundleId);
    };

    const useNetPrice = primaryIsNet(
        internalDisplayConfig.primaryPriceType,
        internalDisplayConfig.configuredPrimaryPrice
    );

    const deliveryDate = extractDeliveryDate(
        selectedStoreData?.selectedDeliveryMode,
        selectedStoreData?.selectedFulfillmentType,
        selectedStoreData?.possibleDeliveryModes
    );

    const showsSummaryDnrInfo = getShowsSummaryDnrInfo(selectedPriceAndPromotionInfo);

    const augmentedOptionalTrackingTags = {
        ...optionalTrackingTags,
        showsSummaryDnrInfo: showsSummaryDnrInfo
    };

    const bundleSelectorTrackingTags = {
        ...optionalTrackingTags,
        originalBettyVariantBundleId: preselectedBundleId
    };

    const pricesDisplayData = getPricesDisplayData(
        sortedBundleSelector,
        variant.bundles,
        priceAndPromotionInfos,
        internalDisplayConfig
    );

    return (
        <>
            {jsxWithIntl(
                <div className="sd-articlecard">
                    <div className="top-part">
                        <PromotionLabelList
                            promotionLabels={selectedPriceAndPromotionInfo?.promotionLabels}
                            dnrInfo={selectedPriceAndPromotionInfo?.dnrInfo}
                            flashSalesCounter={selectedPriceAndPromotionInfo?.flashSalesCounter}
                            recentlyOrdered={internalDisplayConfig?.recentlyOrdered}
                            maxPromotionLabelsShown={internalDisplayConfig?.maxPromotionLabelsShown}
                            recentlyOrderedDateString={internalDisplayConfig?.recentlyOrderedDateString}
                        />
                        <div className="image-container">
                            <ImageDisplay
                                description={selectedBundle?.description}
                                pdpLink={pdpLink}
                                eventId={eventId}
                                bettyBundleId={selectedBundle?.bettyBundleId?.bettyBundleId}
                                imageUrl={selectedBundle?.imageUrl}
                                variant={variant?.allImageUrls}
                                fallbackImageUrl={selectedBundle?.fallbackImageUrl}
                                onArticleOpen={callbacks.onArticleOpen}
                                onSuccessfulImageLoad={callbacks.onSuccessfulImageLoad}
                                optionalTrackingTags={augmentedOptionalTrackingTags}
                            />
                            <AddToListDropdown
                                onCreateNewListCallback={onAddToListCreateNewList}
                                bettyBundleId={selectedBundle?.bettyBundleId?.bettyBundleId || ""}
                                bettyVariantId={selectedBundle?.bettyBundleId?.bettyVariantId || ""}
                                articleDeprecated={articleDeprecated}
                                onAddToListCallback={onListMenuItemSelect}
                                onRemoveFromListCallback={onListMenuItemSelect}
                                selectedDeliveryAddress={selectedDeliveryAddress}
                                isLoggedIn={displayConfig.isLoggedIn}
                                isCustomer={displayConfig.isCustomer}
                                isEmployee={displayConfig.isEmployee}
                                customerTier={displayConfig.customerTier}
                                isReplacementSearch={displayConfig.isReplacementSearch}
                                nonExistentBundle={displayConfig.nonExistentBundle}
                                addToListDefaultGloballyVisible={displayConfig.addToListDefaultGloballyVisible}
                                compulsoryTrackingTags={{
                                    originEventId: eventId,
                                    pageType: (displayConfig.pageType || "unknown") as PageType
                                }}
                                optionalTrackingTags={augmentedOptionalTrackingTags as BasketAddTrackingTags}
                            />
                        </div>
                        <Description
                            description={selectedBundle?.description}
                            pdpLink={pdpLink}
                            eventId={eventId}
                            bettyBundleId={selectedBundle?.bettyBundleId?.bettyBundleId}
                            optionalTrackingTags={augmentedOptionalTrackingTags}
                            onArticleOpen={callbacks.onArticleOpen}
                        />
                        <div className="bundle-selector-customer-id-block">
                            <BundleSelectorDropdown
                                selectedBundleNumber={selectedBundle?.bettyBundleId?.bundleNumber || ""}
                                bundles={variant.bundles}
                                pricesDisplayData={pricesDisplayData}
                                bundleSelector={sortedBundleSelector}
                                onBundleSelect={onBundleSelect}
                                compulsoryTrackingTags={{
                                    originEventId: eventId,
                                    pageType: (displayConfig.pageType || "unknown") as PageType
                                }}
                                optionalTrackingTags={bundleSelectorTrackingTags}
                            />
                            <CustomerDisplayId
                                customerDisplayId={selectedBundle?.customerDisplayId}
                                isEmployee={internalDisplayConfig?.isEmployee}
                            />
                        </div>
                    </div>
                    <AvailabilityDetails
                        availability={selectedBundle?.availability}
                        deliveryMode={selectedStoreData?.selectedDeliveryMode}
                        deliveryDate={deliveryDate}
                        delisted={selectedStoreData?.delisted}
                        storeName={internalDisplayConfig?.storeName}
                    />
                    <div className="bottom-part">
                        <DnRDisplay
                            priceAndPromotionInfo={selectedPriceAndPromotionInfo}
                            weightPerPiece={selectedBundle?.weightPerPiece}
                            amountInBasket={selectedAmountInDnr}
                            isWeightArticle={selectedBundle?.isWeightArticle === "WEIGHT"}
                            useNetPrice={useNetPrice}
                            bettyBundleId={selectedBundle?.bettyBundleId?.bettyBundleId}
                            compulsoryTrackingTags={{
                                originEventId: eventId,
                                pageType: (displayConfig.pageType || "unknown") as PageType
                            }}
                            optionalTrackingTags={augmentedOptionalTrackingTags}
                        />
                        <PriceDisplay
                            priceAndPromotionInfo={selectedPriceAndPromotionInfo}
                            internalDisplayConfig={internalDisplayConfig}
                            isWeightedArticleRaw={selectedBundle?.isWeightArticle}
                            bettyBundleId={selectedBundle?.bettyBundleId?.bettyBundleId || ""}
                            isSingletonBundle={selectedBundle?.bundleSize === "1"}
                            // Workaround for broken per-piece prices for leading articles as they
                            // do not have correct bundle sizes due to a missing link with the
                            // Leading Article Service. Can be removed when BTP-HV3QO5 is closed.
                            isLeadingArticle={selectedBundle?.displayId?.startsWith("A") || false}
                        />
                    </div>
                    <AddToBasketControl
                        onAddToCart={onAddToCart}
                        selectedBundle={selectedBundle}
                        priceAndPromotionInfo={selectedPriceAndPromotionInfo}
                        internalDisplayConfig={internalDisplayConfig}
                        selectedStoreData={selectedStoreData}
                        amountInBasket={selectedAmountInBasket}
                        articleDeprecated={articleDeprecated}
                        enabled={basketInfo.enabled}
                        tabIndex={basketInfo.tabIndex}
                    />
                    {debugInformation && (
                        <SearchResultDebugInfoPanel
                            debugInfo={debugInformation.debugInfo}
                            ltrDebugInfo={debugInformation.ltrDebugInfo}
                            isEmployee={internalDisplayConfig?.isEmployee}
                        />
                    )}
                </div>
            )}
        </>
    );
}

export default ArticleCard;
